/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { loadVGSCollect } from '@vgs/collect-js';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Button } from './ui/Button';
import { EventType, isTokenizePaymentMessage } from '@vertical-insure/shared';
import { useOfferContext } from '@/lib/hooks/useOfferContext';
import { useFormContext } from 'react-hook-form';
import { completePurchase } from '@/lib/actions';

export const PaymentForm = () => {
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const { config, getSelectedQuotes } = useOfferContext();
  const [paymentFormInstance, setPaymentFormInstance] = useState<any>();
  const [isPaymentFormSubmitting, setPaymentFormSubmitting] = useState(false);
  const vgsInitialized = useRef(false)
  const form = useFormContext();

  const isTest = config?.is_test ?? true;
  const vaultId = isTest ? process.env.NEXT_PUBLIC_VGS_TEST_VAULT_ID! : process.env.NEXT_PUBLIC_VGS_LIVE_VAULT_ID!;

  useEffect(() => {
    const handler = async (ev: MessageEvent<unknown>) => {
      if (isTokenizePaymentMessage(ev.data)) {
        tokenize();
      }
    }

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler)
  });
  
  
  useEffect(() => {
    const css = {
      fontFamily: '"Helvetica Neue", Helvetica',
      boxSizing: 'border-box',
      lineHeight: '1.5em',
      fontSize: '14px',
      fontWeight: '200',
      border: 'none',
      color: '#31325F',
      width: '100%',
      height: '100%',
      '&::placeholder': {
        color: '#CFD7E0',
      },
    };

    // Add global styles for iframes
    const style = document.createElement('style');
    style.textContent = `
      iframe {
        width: 100%;
        height: 100%;
      },
    `;
    document.head.appendChild(style);

    const initForm = (vgs_collect: any) => {
      const form = vgs_collect.init(() => {});
      form.field('#card-number', {
        type: 'card-number',
        name: 'card_number',
        successColor: '#4F8A10',
        errorColor: '#D8000C',
        placeholder: '1234 1234 1234 1234',
        showCardIcon: true,
        validations: ['required', 'validCardNumber'],
        autoComplete: 'cc-number',
        css: {
          ...css,
          boxSizing: 'border-box',
          'padding-right': '0',
        },
      });
      form.field('#card-cvc', {
        type: 'card-security-code',
        name: 'card_cvc',
        successColor: '#4F8A10',
        errorColor: '#D8000C',
        placeholder: 'CVC',
        validations: ['required', 'validCardSecurityCode'],
        css,
      });
      form.field('#card-expiry', {
        type: 'card-expiration-date',
        name: 'card_exp',
        successColor: '#4F8A10',
        errorColor: '#D8000C',
        placeholder: 'MM / YY',
        validations: ['required', 'validCardExpirationDate'],
        autoComplete: 'cc-exp',
        css,
      });
      form.field('#card-postal-code', {
        type: "text",
        name: 'postal_code',
        successColor: '#4F8A10',
        errorColor: '#D8000C',
        placeholder: '12345',
        validations: ['required', 'postal_code/us'],
        autoComplete: 'postal-code',
        css,
      });
      setPaymentFormInstance(form);
    };
  

    const loadForm = async () => {
      const vgs_collect = await loadVGSCollect({
        vaultId,
        environment: isTest ? 'sandbox' : 'live',
        version: '2.27.2',
      }).catch((e) => {
        console.log(e);
      });
      initForm(vgs_collect);
    };

    if (!vgsInitialized.current) {
      vgsInitialized.current = true;
      loadForm();
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const results = await tokenize();
    if (results?.token) {
      const formData = new FormData(e.target as HTMLFormElement);
      formData.append('payment_token', results.token);
      const res = await completePurchase(formData);
      if ('error' in res) {
        setError(res.error);
      }
    } 
    setLoading(false);
  }

  const tokenize = async () => {
    if (config?.origin) {
      setError(undefined);
      
      const valid = await form.trigger();

      if (!valid) {
        window.parent.postMessage({
          eventType: EventType.tokenize_payment,
          is_valid: false
        }, config.origin);
      }

      if (!isPaymentFormSubmitting && paymentFormInstance) {
        setPaymentFormSubmitting(true);

        try {
          const token = await new Promise<string>((resolve, reject) => {
            paymentFormInstance.submit(
              '/post',
              {},
              (status: number, data: any) => {
                if (status === 200) {
                  const token = `vgs:${data.card_number}:${btoa(data.card_exp)}:${data.card_cvc}:${btoa(data.postal_code)}`
                  resolve(token);
                } else {
                  reject(new Error('Failed to tokenize payment'));
                }
              },
              (errors: any) => {
                if (errors.card_number) {
                  reject(new Error(`Your card number ${errors.card_number.errorMessages[0]}`));
                } else if (errors.card_exp) {
                  reject(new Error(`Your card expiration date ${errors.card_exp.errorMessages[0]}`));
                } else if (errors.card_cvc) {
                  reject(new Error(`Your card CVC ${errors.card_cvc.errorMessages[0]}`));
                } else if (errors.postal_code) {
                  reject(new Error(`Your card postal code ${errors.postal_code.errorMessages[0]}`));
                }
                reject(new Error(Object.values(errors).map(err => (err as any).errorMessages?.[0]).filter(err => !!err).join(', ')));
              }
            );
          });

          window.parent.postMessage({
            eventType: EventType.tokenize_payment,
            token,
          }, config.origin);
    
          return {
            token,
          }
        } catch (err) {
          const error = err as Error;
          window.parent.postMessage({
            eventType: EventType.tokenize_payment,
            error: {
              code: 'validation_error',
              message: error.message
            }
          }, config.origin);

          setError(error.message);

          return {
            error: error.message
          };
        } finally {
          setPaymentFormSubmitting(false);
        }
      }
    }
  }

  // grab selected quotes
  const acceptedQuoteIds = form?.getValues('products')
    .map((p: { accepted_quotes: string[]; }) => p.accepted_quotes).flat();
  const selectedQuotes = getSelectedQuotes(acceptedQuoteIds);

  return (
    <>
      <div className="mt-2 border rounded-xl px-2 py-3">
        <form id="cc-form">
          <div className="text-gray-500 font-light h-30">
            <div className="bg-transparent font-light outline-none cursor-text h-15 mb-2">
              <label htmlFor="card-number" className="text-xs font-medium h-5">Card Number</label>
              <div id="card-number" className="bg-transparent font-light outline-none cursor-text border pr-2 pl-2 border-gray-200 rounded-md h-10"></div>
            </div>
            <div className="flex flex-row h-15 mb-2">
              <div className="flex-1 h-15 mr-2">
                <label htmlFor="card-expiry" className="text-xs font-medium h-5">Expiration Date</label>
                <div id="card-expiry" className="bg-transparent font-light outline-none cursor-text border pr-2 pl-2 border-gray-200 rounded-md h-10"></div>
              </div>
              <div className="flex-1 h-15 ml-2">
                <label htmlFor="card-cvc" className="text-xs font-medium h-5">Security Code</label>
                <div id="card-cvc" className="bg-transparent font-light outline-none cursor-text border pr-2 pl-2 border-gray-200 rounded-md h-10"></div>
              </div>  
            </div>
            <div className="bg-transparent font-light outline-none cursor-text h-15 mb-2">
              <label htmlFor="card-postal-code" className="text-xs font-medium h-5">ZIP code</label>
              <div id="card-postal-code" className="bg-transparent font-light outline-none cursor-text border pr-2 pl-2 border-gray-200 rounded-md h-10"></div>
            </div>
          </div>
        </form>
      </div>
      {error &&
        <div className="text-sm py-1 text-error">{error}</div>
      }

      {config?.payments?.button && 
        <form className="space-y-2" id="purchase-form" onSubmit={handleSubmit}>
          <input type="hidden" name="product_quotes" value={JSON.stringify(selectedQuotes)} />
          <input type="hidden" name="is_test" value={isTest.toString()} />
          <div className="flex justify-end">
            <Button disabled={loading} isLoading={loading}>Purchase Coverage</Button>
          </div>
        </form>
      }
    </>
  )
}

export const VGSPaymentComponent = () => {
  const { findFirstQuote } = useOfferContext();
  const anyQuote = findFirstQuote();
  

  if (!anyQuote) { 
    return null;
  }

  return (
    <PaymentForm />
  )
}